// web/src/pages/Inventory/InventoryPro.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box, Paper, Typography, Stack, Button, Chip, Divider, TextField,
  Drawer, Tabs, Tab, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, CircularProgress, Tooltip, Snackbar, Alert, Table, TableBody, TableRow, TableCell
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete, Close, Visibility, UploadFile, Download, InfoOutlined } from "@mui/icons-material";
import * as XLSX from "xlsx";

import {
  useListItemsQuery,
  useCreateItemMutation,
  useDeleteItemMutation,
  useStockInMutation,
  useListItemUnitsQuery,
  useListItemTxnsByItemQuery,
  useLazySearchOwnersQuery,
} from "../../redux/admin/inventoryApi";
import { useGetDeviceModelsQuery } from "../../redux/admin/deviceApi";
import { downloadInventoryTemplate, uploadInventoryFile } from "../../services/inventoryImportService";

const CATEGORY_OPTIONS = [
  { value: "gps", label: "GPS Tracker" },
  { value: "sim", label: "SIM Card" },
  { value: "fastag", label: "FASTag" },
  { value: "tyre", label: "Tyre" },
  { value: "battery", label: "Battery" },
  { value: "sensor", label: "Sensor" },
  { value: "tool", label: "Tool / Accessory" },
  { value: "general", label: "General Item" },
];
const CATEGORY_LABELS = Object.fromEntries(CATEGORY_OPTIONS.map((option) => [option.value, option.label]));
const OWNER_TYPES = [
  { value: "warehouse", label: "Warehouse", description: "Central stock. Kisi ko assign nahi hoga." },
  { value: "dealer", label: "Dealer / Branch", description: "Dealer ya branch-level stock handover." },
  { value: "employee", label: "Employee / Staff", description: "Field, office, HR ya manager staff ko issue karo." },
  { value: "user", label: "Customer / User", description: "End customer account ko assign karo." },
  { value: "device", label: "Device Record", description: "Tracker/device record se directly link karo." },
  { value: "vehicle", label: "Vehicle", description: "Vehicle installation ya vehicle-wise inventory tracking." },
];
const IMPORT_PRESETS = [
  {
    value: "general_bulk",
    label: "General Item Bulk",
    categoryLabel: "General Item",
    typeLabel: "Bulk Qty",
    columns: ["name", "quantity", "unit", "minStock", "sku", "notes"],
    steps: [
      "Use one row per stock item.",
      "Quantity must be greater than 0.",
      "Same item name uploads will add stock into that item.",
    ],
    instructions: {
      hinglish: [
        "Ye format general stock items ke liye hai jaise relay, wire, accessories, screws.",
        "Har row me ek item rakho aur `quantity` me total count likho.",
        "Agar same item inventory me pehle se hai to stock add ho jayega.",
        "Upload se pehle template hi use karo, random columns mat banao.",
      ],
      english: [
        "Use this format for quantity-based stock like accessories, wire, relay, screws.",
        "Keep one item per row and enter the total count in `quantity`.",
        "If the item already exists, the uploaded quantity will be added to current stock.",
        "Always use the template columns before uploading.",
      ],
    },
  },
  {
    value: "battery_bulk",
    label: "Battery Bulk",
    categoryLabel: "Battery",
    typeLabel: "Bulk Qty",
    columns: ["name", "quantity", "unit", "minStock", "sku", "notes"],
    steps: [
      "Battery count upload ke liye use karo.",
      "Agar item already hai to uski stock quantity badhegi.",
      "One-by-one serial battery tracking ke liye manual serialized item use karo.",
    ],
    instructions: {
      hinglish: [
        "Ye battery quantity upload ke liye hai, ek-ek battery serial tracking ke liye nahi.",
        "Har row me battery item name aur quantity do.",
        "Existing battery item milega to uski quantity badh jayegi.",
      ],
      english: [
        "Use this for quantity-based battery upload, not one-by-one serial tracking.",
        "Enter battery item name and quantity in each row.",
        "If the battery item already exists, stock will increase on upload.",
      ],
    },
  },
  {
    value: "tyre_bulk",
    label: "Tyre Bulk",
    categoryLabel: "Tyre",
    typeLabel: "Bulk Qty",
    columns: ["name", "quantity", "unit", "minStock", "sku", "notes"],
    steps: [
      "Tyre stock count upload ke liye use karo.",
      "Quantity numeric honi chahiye.",
    ],
    instructions: {
      hinglish: [
        "Tyre stock count upload ke liye har row me tyre item aur quantity likho.",
        "Quantity 0 se badi honi chahiye.",
      ],
      english: [
        "Use one row per tyre stock item with a positive quantity.",
        "Quantity must be greater than zero.",
      ],
    },
  },
  {
    value: "tool_bulk",
    label: "Tool / Accessory Bulk",
    categoryLabel: "Tool / Accessory",
    typeLabel: "Bulk Qty",
    columns: ["name", "quantity", "unit", "minStock", "sku", "notes"],
    steps: [
      "Relay, wire, accessories jaise count-based items ke liye best hai.",
      "Same item names merge ho kar stock add karenge.",
    ],
    instructions: {
      hinglish: [
        "Tools aur accessories jaise count-based stock ke liye best format hai.",
        "Same item name dobara upload hua to quantity merge/add hogi.",
      ],
      english: [
        "Best for count-based tools and accessories.",
        "Repeated item names will merge and increase stock quantity.",
      ],
    },
  },
  {
    value: "fastag_bulk",
    label: "FASTag Bulk",
    categoryLabel: "FASTag",
    typeLabel: "Bulk Qty",
    columns: ["name", "quantity", "unit", "minStock", "sku", "notes"],
    steps: [
      "FASTag sticker ya count-based tag stock ke liye use karo.",
      "Quantity positive honi chahiye.",
    ],
    instructions: {
      hinglish: [
        "FASTag count upload ke liye use karo.",
        "Har row me item name aur positive quantity do.",
      ],
      english: [
        "Use this for FASTag count uploads.",
        "Provide item name and a positive quantity in each row.",
      ],
    },
  },
  {
    value: "gps_serialized",
    label: "GPS / Device IMEI",
    categoryLabel: "GPS Tracker",
    typeLabel: "Serialized",
    columns: ["name", "imei", "modelType", "sku", "notes"],
    steps: [
      "Har row me ek device hoga.",
      "IMEI exactly 15 digits hona chahiye.",
      "Model type required hai.",
    ],
    instructions: {
      hinglish: [
        "GPS / device upload me har row ek alag device hota hai.",
        "IMEI exactly 15 digits hona chahiye aur duplicate nahi hona chahiye.",
        "`modelType` required hai, warna upload fail hoga.",
      ],
      english: [
        "Each row represents one GPS/device unit.",
        "IMEI must be exactly 15 digits and must not be duplicated.",
        "`modelType` is required for every row.",
      ],
    },
  },
  {
    value: "sim_serialized",
    label: "SIM Card",
    categoryLabel: "SIM Card",
    typeLabel: "Serialized",
    columns: ["name", "simNumber", "imsiNumber", "provider", "simType", "iotSimNumber", "sku", "notes"],
    steps: [
      "Har row me ek SIM card hoga.",
      "SIM number, IMSI, provider, sim type, IoT sim sab required hain.",
      "SIM number duplicate nahi hona chahiye.",
    ],
    instructions: {
      hinglish: [
        "SIM upload me har row ek alag SIM card hai.",
        "`simNumber`, `imsiNumber`, `provider`, `simType`, `iotSimNumber` sab required hain.",
        "Duplicate SIM number file me ya inventory me hua to upload reject hoga.",
      ],
      english: [
        "Each row represents one SIM card.",
        "`simNumber`, `imsiNumber`, `provider`, `simType`, and `iotSimNumber` are all required.",
        "Duplicate SIM numbers in the file or inventory will be rejected.",
      ],
    },
  },
  {
    value: "sensor_serialized",
    label: "Sensor Serial",
    categoryLabel: "Sensor",
    typeLabel: "Serialized",
    columns: ["name", "serial", "sku", "notes"],
    steps: [
      "Har row me ek sensor hoga.",
      "Serial unique hona chahiye.",
    ],
    instructions: {
      hinglish: [
        "Sensor upload me har row ek sensor unit hai.",
        "`serial` unique hona chahiye, duplicate allowed nahi hai.",
      ],
      english: [
        "Each row represents one sensor unit.",
        "`serial` must be unique; duplicates are not allowed.",
      ],
    },
  },
];
const normalizeImportValue = (value) => String(value ?? "").trim();
const getImportPresetForItem = (item = {}) => {
  const category = String(item?.category || "general").trim().toLowerCase();
  if (category === "gps") return "gps_serialized";
  if (category === "sim") return "sim_serialized";
  if (category === "sensor") return "sensor_serialized";
  if (category === "battery") return "battery_bulk";
  if (category === "tyre") return "tyre_bulk";
  if (category === "fastag") return "fastag_bulk";
  if (category === "tool") return "tool_bulk";
  return "general_bulk";
};

function getCounts(it) {
  const row = it || {};
  const units = Array.isArray(row.units) ? row.units : [];
  const assignedCount = row.isSerialized
    ? units.filter(u => u?.status === "assigned").length
    : units
        .filter((u) => u?.status === "assigned")
        .reduce((sum, unit) => sum + Math.max(1, Number(unit?.quantity || 1)), 0);

  const inStock = row.isSerialized
    ? units.filter(u => u?.status === "in_stock").length
    : Number(row.quantityOnHand || 0);

  return { assignedCount, inStock };
}

function formatOwnerDisplay(owner = {}) {
  const label = String(owner?.ownerLabel || "").trim() || "Warehouse";
  const type = String(owner?.ownerType || "").trim();
  if (!type || type === "warehouse") return label;
  const typeLabel = OWNER_TYPES.find((option) => option.value === type)?.label || type;
  return `${label} • ${typeLabel}`;
}

function formatUnitStatus(unit = {}) {
  const status = String(unit?.status || "").trim().toLowerCase();
  const owner = unit?.currentOwner || {};
  const ownerLabel = String(owner?.ownerLabel || "").trim();

  if (status === "assigned") {
    if (owner?.ownerType === "vehicle" && ownerLabel) {
      return `Assigned to vehicle: ${ownerLabel}`;
    }
    if (ownerLabel) {
      return `Assigned to ${ownerLabel}`;
    }
    return "Assigned";
  }

  if (status === "in_stock") return "In Stock";
  if (status === "maintenance") return "Under Maintenance";
  if (status === "lost") return "Lost";
  return status ? status.replaceAll("_", " ") : "Unknown";
}

function formatTxnTypeLabel(txnType = "") {
  const value = String(txnType || "").trim().toLowerCase();
  if (!value) return "Update";
  const labels = {
    stock_in: "Stock In",
    assign: "Issued",
    transfer: "Transfer",
    return: "Returned",
    handover: "Handed Over",
    consume: "Consumed",
  };
  return labels[value] || value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTxnReference(txn = {}) {
  const parts = [
    txn?.imei,
    txn?.serial && txn?.serial !== txn?.imei ? txn.serial : "",
    txn?.simNumber,
    txn?.imsiNumber,
    txn?.provider,
  ].filter(Boolean);
  return parts.join(" • ") || "—";
}

function formatTxnDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatTaskContextRows(txn = {}) {
  const task = txn?.taskInfo || {};
  const rows = [
    { label: "Task", value: task?.title || "" },
    { label: "Vehicle", value: task?.vehicleNumber || "" },
    { label: "IMEI", value: task?.imei || "" },
    { label: "SIM No", value: task?.simNumber || "" },
    { label: "User", value: task?.userLabel || task?.userName || "" },
    { label: "Created By", value: task?.createdByLabel || task?.createdByName || txn?.actor?.label || "" },
    { label: "Model", value: task?.model || "" },
    { label: "Contact", value: task?.contact || "" },
    { label: "Creation Time", value: task?.createdAt ? formatTxnDateTime(task.createdAt) : "" },
    { label: "License Type", value: task?.licenseType || "" },
    { label: "From", value: formatOwnerDisplay(txn?.from) || "" },
    { label: "To", value: formatOwnerDisplay(txn?.to) || "" },
  ].filter((row) => String(row.value || "").trim());

  return rows.length ? rows : [{ label: "Details", value: txn?.notes || "No extra task information" }];
}

function UploadInstructionsDialog({ open, onClose, title, subtitle, requiredColumns = [], hinglish = [], english = [] }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        ) : null}

        {requiredColumns.length ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Required columns: {requiredColumns.join(", ")}
          </Alert>
        ) : null}

        <Typography fontWeight={800} sx={{ mb: 1 }}>Hinglish Guide</Typography>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {hinglish.map((line, index) => (
              <TableRow key={`hi-${index}`}>
                <TableCell sx={{ width: 42, fontWeight: 800 }}>{index + 1}</TableCell>
                <TableCell>{line}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography fontWeight={800} sx={{ mb: 1 }}>English Guide</Typography>
        <Table size="small">
          <TableBody>
            {english.map((line, index) => (
              <TableRow key={`en-${index}`}>
                <TableCell sx={{ width: 42, fontWeight: 800 }}>{index + 1}</TableCell>
                <TableCell>{line}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ✅ DataGrid safe helper
const safeRow = (params) => params?.row || {};
const isGpsCategory = (category) => String(category || "").toLowerCase() === "gps";
const isSimCategory = (category) => String(category || "").toLowerCase() === "sim";
const isElectronicsCategory = (category) => isGpsCategory(category) || isSimCategory(category) || String(category || "").toLowerCase() === "sensor";
const getCategoryLabel = (category) => CATEGORY_LABELS[String(category || "general").toLowerCase()] || "General Item";

export default function InventoryPro() {
  const [q, setQ] = useState("");
  const { data, isLoading, isFetching, refetch } = useListItemsQuery({ q });
  const { data: deviceModelOptions } = useGetDeviceModelsQuery();
  const [lastCreatedItemId, setLastCreatedItemId] = useState("");
  const [lastCreatedItemName, setLastCreatedItemName] = useState("");

  const items = Array.isArray(data?.items) ? data.items : [];

  const [createItem, { isLoading: creating }] = useCreateItemMutation();
  const [deleteItem, { isLoading: deleting }] = useDeleteItemMutation();
  const [stockIn, { isLoading: stocking }] = useStockInMutation();

  const busy = creating || deleting || stocking;

  // dialogs
  const [dialog, setDialog] = useState(null); // {type, item}
  const closeDialog = () => setDialog(null);

  // drawer
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(0);

  // toast
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const showToast = (type, msg) => setToast({ open: true, type, msg });

  // quick add
  const [qaName, setQaName] = useState("");
  const [qaCategory, setQaCategory] = useState("general");
  const [qaSerialized, setQaSerialized] = useState(false);
  const [qaExtra, setQaExtra] = useState({
    imei: "",
    modelType: "",
    simNumber: "",
    imsiNumber: "",
    provider: "",
    simType: "",
    iotSimNumber: "",
  });
  const [importPreset, setImportPreset] = useState("gps_serialized");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validatingImport, setValidatingImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [topInstructionOpen, setTopInstructionOpen] = useState(false);
  const importInputRef = useRef(null);

  const selectedImportPreset = useMemo(
    () => IMPORT_PRESETS.find((preset) => preset.value === importPreset) || IMPORT_PRESETS[0],
    [importPreset]
  );

  const stats = useMemo(() => {
    const total = items.length;
    const low = items.filter(i => !i?.isSerialized && (i?.quantityOnHand || 0) <= (i?.minStock || 0)).length;
    return { total, low };
  }, [items]);

  const availableDeviceModels = useMemo(() => {
    const models = Array.isArray(deviceModelOptions?.allModels) ? deviceModelOptions.allModels : [];
    return Array.from(new Set(models.map((model) => String(model).trim()).filter(Boolean)));
  }, [deviceModelOptions]);

  const columns = useMemo(() => ([
    {
      field: "name",
      headerName: "Item",
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const row = safeRow(params);
        const usedVehicles = Array.from(
          new Set(
            (Array.isArray(row.units) ? row.units : [])
              .filter((unit) => unit?.currentOwner?.ownerType === "vehicle" && unit?.currentOwner?.ownerLabel)
              .map((unit) => String(unit.currentOwner.ownerLabel).trim())
              .filter(Boolean)
          )
        );

        return (
          <Stack spacing={0.3} sx={{ py: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {row.name}
            </Typography>
            {usedVehicles.slice(0, 2).map((label) => (
              <Typography key={label} variant="caption" color="text.secondary">
                Used for vehicle: {label}
              </Typography>
            ))}
            {usedVehicles.length > 2 ? (
              <Typography variant="caption" color="text.secondary">
                +{usedVehicles.length - 2} more vehicles
              </Typography>
            ) : null}
          </Stack>
        );
      },
    },

    {
      field: "category",
      headerName: "Category",
      width: 140,
      valueGetter: (params) => {
        const row = safeRow(params);
        return getCategoryLabel(row.category);
      },
    },

    {
      field: "type",
      headerName: "Type",
      width: 130,
      valueGetter: (params) => {
        const row = safeRow(params);
        return row.isSerialized ? "SERIALIZED" : "BULK";
      },
    },

    {
      field: "inStock",
      headerName: "In Stock",
      width: 120,
      valueGetter: (params) => getCounts(safeRow(params)).inStock,
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Open inventory item">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                if (!params?.row?._id) return;
                setSelected(params.row);
                setTab(0);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Stock in">
            <span>
              <IconButton
                size="small"
                color="success"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!params?.row?._id) return;
                  setSelected(params.row);
                  setTab(0);
                  setDialog({ type: "stockin", item: params.row });
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [busy]);

  const onCreate = async () => {
    const name = qaName.trim();
    if (!name) return showToast("warning", "Name required");
    if (qaCategory === "gps" && !qaSerialized) {
      return showToast("warning", "GPS inventory items must remain serialized.");
    }
    if (isGpsCategory(qaCategory) && !qaExtra.imei.trim()) {
      return showToast("warning", "IMEI is required for GPS inventory.");
    }
    if (isGpsCategory(qaCategory) && !qaExtra.modelType.trim()) {
      return showToast("warning", "Model type is required for GPS inventory.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.simNumber.trim()) {
      return showToast("warning", "SIM ke liye sim number required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.imsiNumber.trim()) {
      return showToast("warning", "SIM ke liye IMSI number required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.provider.trim()) {
      return showToast("warning", "SIM ke liye provider required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.simType.trim()) {
      return showToast("warning", "SIM ke liye sim type required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.iotSimNumber.trim()) {
      return showToast("warning", "SIM ke liye actual IoT sim number required hai.");
    }

    try {
      const response = await createItem({
        name,
        category: qaCategory,
        itemType: qaCategory,
        isSerialized: qaSerialized,
        unit: "pcs",
        minStock: 0,
        quantityOnHand: qaSerialized ? 0 : 1,
        modelType: qaExtra.modelType,
        simNumber: qaExtra.simNumber,
        imsiNumber: qaExtra.imsiNumber,
        provider: qaExtra.provider,
        simType: qaExtra.simType,
        iotSimNumber: qaExtra.iotSimNumber,
        units: qaSerialized && (isGpsCategory(qaCategory) || isSimCategory(qaCategory))
          ? [{
              serial: isGpsCategory(qaCategory) ? qaExtra.imei : qaExtra.simNumber,
              imei: qaExtra.imei,
              modelType: qaExtra.modelType,
              simNumber: qaExtra.simNumber,
              imsiNumber: qaExtra.imsiNumber,
              provider: qaExtra.provider,
              simType: qaExtra.simType,
              iotSimNumber: qaExtra.iotSimNumber,
            }]
          : [],
      }).unwrap();

      setQaName("");
      setQaCategory("general");
      setQaSerialized(false);
      setQaExtra({
        imei: "",
        modelType: "",
        simNumber: "",
        imsiNumber: "",
        provider: "",
        simType: "",
        iotSimNumber: "",
      });
      setQ("");
      setLastCreatedItemId(String(response?.item?._id || ""));
      setLastCreatedItemName(String(response?.item?.name || name));
      if (response?.item?._id) {
        setSelected(response.item);
        setTab(0);
      }
      refetch();
      showToast("success", "Inventory item added. It is visible in the list below.");
    } catch (e) {
      showToast("error", e?.data?.msg || e?.message || "Create failed");
    }
  };

  const onDelete = async (item) => {
    try {
      await deleteItem(item._id).unwrap();
      showToast("success", "Item deleted");
      closeDialog();
      if (selected?._id === item._id) setSelected(null);
    } catch (e) {
      showToast("error", e?.data?.msg || e?.message || "Delete failed");
    }
  };

  const syncSelectedItem = (updatedItem) => {
    if (!updatedItem?._id) return;
    setSelected((prev) => (prev?._id === updatedItem._id ? updatedItem : prev));
  };

  const resetImportSelection = () => {
    setImportFile(null);
    if (importInputRef.current) importInputRef.current.value = "";
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadInventoryTemplate(importPreset);
      showToast("success", `${selectedImportPreset.label} template downloaded`);
    } catch (error) {
      showToast("error", error?.response?.data?.msg || "Template download failed");
    }
  };

  const handleValidateImport = async () => {
    if (!importFile) {
      showToast("warning", "Upload karne se pehle file select karo");
      return;
    }
    setValidatingImport(true);
    try {
      const response = await uploadInventoryFile(importPreset, importFile, { validateOnly: true });
      setImportResult({
        type: "success",
        message: response?.msg || "File validated successfully",
        importedCount: response?.importedCount || 0,
        skippedCount: response?.skippedCount || 0,
        touchedItems: response?.touchedItems || [],
        errors: [],
      });
      showToast("success", "File format sahi hai. Ab final upload kar sakte ho.");
    } catch (error) {
      const payload = error?.response?.data;
      setImportResult({
        type: "error",
        message: payload?.msg || "Validation failed",
        importedCount: payload?.importedCount || 0,
        skippedCount: payload?.skippedCount || 0,
        touchedItems: [],
        errors: Array.isArray(payload?.errors) ? payload.errors : [],
      });
      showToast("error", payload?.msg || "Validation failed");
    } finally {
      setValidatingImport(false);
    }
  };

  const handleUploadImport = async () => {
    if (!importFile) {
      showToast("warning", "Upload karne se pehle file select karo");
      return;
    }
    setImporting(true);
    try {
      const response = await uploadInventoryFile(importPreset, importFile, { validateOnly: false });
      setImportResult({
        type: "success",
        message: response?.msg || "Upload completed",
        importedCount: response?.importedCount || 0,
        skippedCount: response?.skippedCount || 0,
        touchedItems: response?.touchedItems || [],
        errors: [],
      });
      resetImportSelection();
      refetch();
      showToast(
        "success",
        `${response?.importedCount || 0} row upload ho gayi${response?.skippedCount ? `, ${response.skippedCount} skipped` : ""}`
      );
    } catch (error) {
      const payload = error?.response?.data;
      setImportResult({
        type: "error",
        message: payload?.msg || "Upload failed",
        importedCount: payload?.importedCount || 0,
        skippedCount: payload?.skippedCount || 0,
        touchedItems: [],
        errors: Array.isArray(payload?.errors) ? payload.errors : [],
      });
      showToast("error", payload?.msg || "Upload failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={900}>Inventory (Production)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Total: ${stats.total}`} />
          <Chip label={`Low: ${stats.low}`} color={stats.low ? "warning" : "default"} />
          <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      {/* Quick Add */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>Quick Add</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            fullWidth
            value={qaName}
            onChange={(e) => setQaName(e.target.value)}
            placeholder="Item name..."
            disabled={busy}
            onKeyDown={(e) => e.key === "Enter" && onCreate()}
          />
          <TextField
            select
            value={qaCategory}
            onChange={(e) => {
              const nextCategory = e.target.value;
              setQaCategory(nextCategory);
              setQaExtra({
                imei: "",
                modelType: "",
                simNumber: "",
                imsiNumber: "",
                provider: "",
                simType: "",
                iotSimNumber: "",
              });
              if (nextCategory === "gps" || nextCategory === "sensor" || nextCategory === "sim") {
                setQaSerialized(true);
              }
            }}
            sx={{ minWidth: 170 }}
            disabled={busy}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            value={qaSerialized ? "yes" : "no"}
            onChange={(e) => {
              const nextSerialized = e.target.value === "yes";
              if ((qaCategory === "gps" || qaCategory === "sensor" || qaCategory === "sim") && !nextSerialized) {
                showToast("warning", `${qaCategory.toUpperCase()} items must remain serialized.`);
                return;
              }
              setQaSerialized(nextSerialized);
            }}
            sx={{ minWidth: 190 }}
            disabled={busy}
          >
            <MenuItem value="yes">Serialized</MenuItem>
            <MenuItem value="no">Bulk</MenuItem>
          </TextField>
          <Button variant="contained" onClick={onCreate} disabled={busy} startIcon={<Add />}>
            {creating ? <CircularProgress size={18} /> : "Add"}
          </Button>
        </Stack>

        {isGpsCategory(qaCategory) && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              label="IMEI Number *"
              value={qaExtra.imei}
              onChange={(e) => setQaExtra((prev) => ({ ...prev, imei: e.target.value.replace(/\D/g, "").slice(0, 15) }))}
            />
            <TextField
              select
              fullWidth
              label="Model Type *"
              value={qaExtra.modelType}
              onChange={(e) => setQaExtra((prev) => ({ ...prev, modelType: e.target.value }))}
              helperText="Uses the same model list available during device registration"
            >
              <MenuItem value="">Select model</MenuItem>
              {availableDeviceModels.map((model) => (
                <MenuItem key={model} value={model}>{model}</MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {isSimCategory(qaCategory) && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
            <TextField fullWidth label="SIM Number *" value={qaExtra.simNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, simNumber: e.target.value }))} />
            <TextField fullWidth label="IMSI Number *" value={qaExtra.imsiNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, imsiNumber: e.target.value }))} />
            <TextField fullWidth label="Provider *" value={qaExtra.provider} onChange={(e) => setQaExtra((prev) => ({ ...prev, provider: e.target.value }))} />
            <TextField fullWidth label="SIM Type *" value={qaExtra.simType} onChange={(e) => setQaExtra((prev) => ({ ...prev, simType: e.target.value }))} />
            <TextField fullWidth label="Actual IoT SIM Number *" value={qaExtra.iotSimNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, iotSimNumber: e.target.value }))} />
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Alert severity="info" sx={{ mb: 2 }}>
          Category select karne se inventory ka behavior change hota hai. `GPS Tracker` aur `SIM Card`
          me serial-specific fields aayengi, jabki `General Item` bulk stock ke liye best hai.
        </Alert>

        <Alert severity="success" sx={{ mb: 2 }}>
          Inventory add karne ke baad item seedha neeche list me aata hai. Yahan sirf stock maintain karo,
          aur field employee ko actual material task flow ke through do.
        </Alert>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderStyle: "dashed" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography fontWeight={800}>Bulk Upload Inventory</Typography>
                <Tooltip title="Upload instructions">
                  <IconButton size="small" onClick={() => setTopInstructionOpen(true)}>
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Excel/CSV ko category ke hisaab se upload karo. Har format ka alag template hai.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleTemplateDownload}
                disabled={importing || validatingImport}
              >
                Download Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={() => importInputRef.current?.click()}
                disabled={importing || validatingImport}
              >
                {importFile ? "Change File" : "Choose File"}
              </Button>
              <input
                ref={importInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={(event) => {
                  const file = event?.target?.files?.[0] || null;
                  setImportFile(file);
                  setImportResult(null);
                }}
              />
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 1.5 }}>
            <TextField
              select
              fullWidth
              label="Upload Format"
              value={importPreset}
              onChange={(e) => {
                setImportPreset(e.target.value);
                setImportResult(null);
                resetImportSelection();
              }}
            >
              {IMPORT_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Selected File"
              value={importFile?.name || ""}
              placeholder="No file selected"
              InputProps={{ readOnly: true }}
            />
          </Stack>

          <Alert severity="info" sx={{ mb: 1.5 }}>
            Format: {selectedImportPreset.categoryLabel} • {selectedImportPreset.typeLabel}
            {" | "}
            Required columns: {selectedImportPreset.columns.join(", ")}
          </Alert>

          <Table size="small" sx={{ mb: 1.5 }}>
            <TableBody>
              {selectedImportPreset.steps.map((step, index) => (
                <TableRow key={`${selectedImportPreset.value}-${index}`}>
                  <TableCell sx={{ width: 42, fontWeight: 800 }}>{index + 1}</TableCell>
                  <TableCell>{step}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              onClick={handleValidateImport}
              disabled={!importFile || importing || validatingImport}
            >
              {validatingImport ? <CircularProgress size={18} /> : "Validate File"}
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadImport}
              disabled={!importFile || importing || validatingImport}
              startIcon={!importing ? <UploadFile /> : null}
            >
              {importing ? <CircularProgress size={18} /> : "Upload Inventory"}
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                resetImportSelection();
                setImportResult(null);
              }}
              disabled={importing || validatingImport}
            >
              Clear
            </Button>
          </Stack>

          {importResult ? (
            <Alert severity={importResult.type === "success" ? "success" : "error"} sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: importResult.errors?.length ? 0.75 : 0 }}>
                {importResult.message}
              </Typography>
              <Typography variant="body2">
                Imported: {importResult.importedCount || 0} • Skipped: {importResult.skippedCount || 0}
              </Typography>
              {Array.isArray(importResult.touchedItems) && importResult.touchedItems.length > 0 ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Items: {importResult.touchedItems.slice(0, 6).join(", ")}
                  {importResult.touchedItems.length > 6 ? "..." : ""}
                </Typography>
              ) : null}
              {Array.isArray(importResult.errors) && importResult.errors.length > 0 ? (
                <Box sx={{ mt: 0.75 }}>
                  {importResult.errors.slice(0, 6).map((errorText, index) => (
                    <Typography key={`${errorText}-${index}`} variant="body2">
                      {index + 1}. {errorText}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Alert>
          ) : null}
        </Paper>

        <UploadInstructionsDialog
          open={topInstructionOpen}
          onClose={() => setTopInstructionOpen(false)}
          title={`Inventory Upload Instructions: ${selectedImportPreset.label}`}
          subtitle="Use this for top-level inventory uploads when you want to create/import stock from an Excel or CSV file."
          requiredColumns={selectedImportPreset.columns}
          hinglish={[
            "Sabse pehle sahi `Upload Format` select karo. Format galat hua to upload fail hoga.",
            "Uske baad `Download Template` dabao aur wahi file format use karo.",
            "Top upload ka use tab karo jab naya item banana ho ya mixed inventory ek saath import karni ho.",
            "Upload se pehle `Validate File` dabana best hai, isse galti pehle hi pakad jayegi.",
            ...(selectedImportPreset.instructions?.hinglish || []),
          ]}
          english={[
            "First select the correct `Upload Format`. A wrong format will fail validation.",
            "Then click `Download Template` and fill data only in that template structure.",
            "Use the top upload when you want to create new items or import mixed inventory in one batch.",
            "It is best to click `Validate File` before the final upload so errors are caught early.",
            ...(selectedImportPreset.instructions?.english || []),
          ]}
        />

        <TextField
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / sku / serial / imei..."
        />
      </Paper>

      {/* Grid */}
      <Paper sx={{ height: 560, p: 1.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 1, pb: 1 }}
        >
          <Box>
            <Typography fontWeight={800}>Inventory Items</Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length} item{items.length === 1 ? "" : "s"} visible
              {q ? ` for "${q}"` : ""}.
            </Typography>
          </Box>
          {lastCreatedItemName ? (
            <Chip
              color="success"
              variant="outlined"
              label={`Last added: ${lastCreatedItemName}`}
            />
          ) : null}
        </Stack>

        {items.length === 0 && !isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Stack spacing={1.2} alignItems="center">
              <Typography variant="h6" fontWeight={800}>
                No inventory items visible
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                Upar `Quick Add` se inventory create karo. Create hote hi item yahin list me dikhega. Agar search chal rahi
                hai to usko clear karo ya `Refresh` dabao.
              </Typography>
            </Stack>
          </Box>
        ) : (
          <DataGrid
            rows={items || []}
            columns={columns}
            getRowId={(r) => r?._id}
            loading={isLoading}
            onRowClick={(params) => {
              if (!params?.row?._id) return;
              setSelected(params.row);
              setTab(0);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            getRowClassName={(params) =>
              String(params?.row?._id || "") === lastCreatedItemId ? "inventory-new-row" : ""
            }
            sx={{
              border: 0,
              "& .inventory-new-row": {
                backgroundColor: "rgba(16, 185, 129, 0.08)",
              },
            }}
          />
        )}
      </Paper>

      {/* Drawer */}
      <ItemDrawer
        open={!!selected}
        item={selected}
        tab={tab}
        setTab={setTab}
        onClose={() => setSelected(null)}
        busy={busy}
        onAction={(type) => setDialog({ type, item: selected })}
      />

      {/* Dialogs */}
      {dialog?.type === "stockin" && (
        <StockInDialog
          open
          item={dialog.item}
          modelOptions={availableDeviceModels}
          busy={stocking}
          onClose={closeDialog}
          onSubmit={async (payload) => {
            try {
              const response = await stockIn({ id: dialog.item._id, ...payload }).unwrap();
              syncSelectedItem(response?.item);
              showToast("success", "Stock updated");
              closeDialog();
            } catch (e) {
              showToast("error", e?.data?.msg || e?.message || "Stock in failed");
            }
          }}
        />
      )}

      {dialog?.type === "delete" && (
        <ConfirmDeleteDialog
          open
          item={dialog.item}
          busy={deleting}
          onClose={closeDialog}
          onConfirm={() => onDelete(dialog.item)}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      >
        <Alert
          severity={toast.type}
          variant="filled"
          onClose={() => setToast(t => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---------------- Drawer ----------------
function ItemDrawer({ open, item, tab, setTab, onClose, busy, onAction }) {
  const itemId = item?._id;

  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: itemId },
    { skip: !itemId || tab !== 1 }
  );

  const { data: txnData, isLoading: txnLoading } = useListItemTxnsByItemQuery(
    { id: itemId },
    { skip: !itemId || tab !== 2 }
  );

  const units = unitsData?.units || [];
  const txns = txnData?.txns || [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "50vw" },
          minWidth: { md: 720 },
          maxWidth: { md: 980 },
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography fontWeight={900} variant="h6">{item?.name || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {getCategoryLabel(item?.category)} • {item?.isSerialized ? "SERIALIZED" : "BULK"}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 1, flexWrap: "wrap" }}>
          <Button startIcon={<Edit />} variant="outlined" disabled={busy} onClick={() => onAction("stockin")}>
            Stock In
          </Button>
          <Tooltip title="Delete item">
            <IconButton color="error" disabled={busy} onClick={() => onAction("delete")}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Overview" />
          <Tab label="Units" />
          <Tab label="Transactions" />
        </Tabs>

        {tab === 0 && <OverviewPanel item={item} />}
        {tab === 1 && <UnitsPanel item={item} units={units} loading={unitsLoading} />}
        {tab === 2 && <TxnPanel txns={txns} loading={txnLoading} />}
      </Box>
    </Drawer>
  );
}

function OverviewPanel({ item }) {
  const c = getCounts(item || {});
  const minimumStock = Number(item?.minStock || 0);
  const assignedCount = c.assignedCount;
  const reorderStatus =
    minimumStock > 0
      ? c.inStock <= minimumStock
        ? `Reorder needed: ${Math.max(0, minimumStock - c.inStock)} short`
        : `Safe stock: ${c.inStock - minimumStock} above minimum`
      : "Minimum stock not set yet";
  const overviewRows = [
    { label: "SKU", value: item?.sku || "-" },
    { label: "Unit", value: item?.unit || "pcs" },
    { label: "In Stock", value: c.inStock },
    { label: "Assigned", value: assignedCount },
    { label: "Minimum Stock Level", value: minimumStock || "Not set" },
    { label: "Stock Summary", value: reorderStatus },
  ];

  if (isGpsCategory(item?.category)) {
    overviewRows.push({ label: "Model Type", value: item?.modelType || "-" });
  }

  if (isSimCategory(item?.category)) {
    overviewRows.push(
      { label: "SIM Number", value: item?.simNumber || "-" },
      { label: "IMSI Number", value: item?.imsiNumber || "-" },
      { label: "Provider", value: item?.provider || "-" },
      { label: "SIM Type", value: item?.simType || "-" },
      { label: "Actual IoT SIM Number", value: item?.iotSimNumber || "-" }
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography fontWeight={800}>Overview</Typography>
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableBody>
              {overviewRows.map((row, index) => (
                <TableRow
                  key={row.label}
                  sx={{
                    "& td": {
                      borderBottom: index === overviewRows.length - 1 ? "none" : undefined,
                    },
                  }}
                >
                  <TableCell sx={{ width: "34%", fontWeight: 800, bgcolor: "action.hover" }}>
                    {row.label}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", wordBreak: "break-word" }}>
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
}

function UnitsPanel({ item, units, loading }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const rx = q ? new RegExp(q, "i") : null;
    return (units || []).filter(u => {
      if (status !== "all" && u?.status !== status) return false;
      if (!rx) return true;
      return rx.test(u?.serial || "") || rx.test(u?.imei || "") || rx.test(u?.currentOwner?.ownerLabel || "");
    });
  }, [units, q, status]);

  const cols = [
    { field: "serial", headerName: "Serial", flex: 1, minWidth: 160 },
    { field: "imei", headerName: "IMEI", flex: 1, minWidth: 160 },
    ...(isGpsCategory(item?.category) ? [{ field: "modelType", headerName: "Model Type", flex: 1, minWidth: 150 }] : []),
    ...(isSimCategory(item?.category)
      ? [
          { field: "simNumber", headerName: "SIM Number", flex: 1, minWidth: 160 },
          { field: "imsiNumber", headerName: "IMSI Number", flex: 1, minWidth: 160 },
          { field: "provider", headerName: "Provider", flex: 1, minWidth: 140 },
          { field: "simType", headerName: "SIM Type", flex: 1, minWidth: 140 },
          { field: "iotSimNumber", headerName: "IoT SIM Number", flex: 1, minWidth: 170 },
        ]
      : []),
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => formatUnitStatus(params?.row),
    },
    {
      field: "owner",
      headerName: "Current Owner",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => formatOwnerDisplay(params?.row?.currentOwner),
    },
  ];

  if (!item?.isSerialized) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography>Bulk item hai. Units list applicable nahi. (Qty only)</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
        <TextField fullWidth value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial/imei/owner..." />
        <TextField select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="in_stock">In Stock</MenuItem>
          <MenuItem value="assigned">Assigned</MenuItem>
          <MenuItem value="maintenance">Maintenance</MenuItem>
          <MenuItem value="lost">Lost</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ height: 380 }}>
        <DataGrid
          rows={filtered || []}
          columns={cols}
          getRowId={(r) => r?._id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
      </Box>
    </Paper>
  );
}

function TxnPanel({ txns, loading }) {
  const cols = [
    {
      field: "createdAt",
      headerName: "Time",
      minWidth: 190,
      flex: 0.9,
      valueGetter: (params) => formatTxnDateTime(params?.row?.createdAt),
    },
    {
      field: "txnType",
      headerName: "Action",
      minWidth: 140,
      flex: 0.8,
      valueGetter: (params) => formatTxnTypeLabel(params?.row?.txnType),
    },
    {
      field: "reference",
      headerName: "Serial / IMEI / SIM",
      minWidth: 280,
      flex: 1.4,
      valueGetter: (params) => formatTxnReference(params?.row),
    },
    {
      field: "from",
      headerName: "From",
      minWidth: 190,
      flex: 1,
      valueGetter: (params) => formatOwnerDisplay(params?.row?.from),
    },
    {
      field: "to",
      headerName: "To",
      minWidth: 220,
      flex: 1.1,
      valueGetter: (params) => formatOwnerDisplay(params?.row?.to),
    },
    {
      field: "taskContext",
      headerName: "Task / Vehicle / User Details",
      minWidth: 470,
      flex: 2.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const rows = formatTaskContextRows(params?.row);
        return (
          <Stack spacing={0.45} sx={{ py: 1, width: "100%" }}>
            {rows.map((row) => (
              <Box key={`${params?.row?._id}-${row.label}`} sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                <Typography
                  variant="caption"
                  sx={{ minWidth: 96, fontWeight: 800, color: "text.primary", lineHeight: 1.4 }}
                >
                  {row.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, whiteSpace: "normal" }}>
                  {row.value || "—"}
                </Typography>
              </Box>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes / Task Link",
      minWidth: 320,
      flex: 1.6,
      renderCell: (params) => (
        <Stack sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "normal", lineHeight: 1.35 }}>
            {params?.row?.notes || "—"}
          </Typography>
        </Stack>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ height: 420 }}>
        <DataGrid
          rows={txns || []}
          columns={cols}
          getRowId={(r) => r?._id}
          loading={loading}
          getRowHeight={() => "auto"}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          sx={{
            "& .MuiDataGrid-cell": {
              alignItems: "flex-start",
              py: 1,
            },
          }}
        />
      </Box>
    </Paper>
  );
}

// ---------------- Dialogs ----------------
function StockInDialog({ open, item, busy, onClose, onSubmit, modelOptions = [] }) {
  const [qty, setQty] = useState(1);
  const [serialsText, setSerialsText] = useState("");
  const [extra, setExtra] = useState({
    imei: "",
    modelType: "",
    simNumber: "",
    imsiNumber: "",
    provider: "",
    simType: "",
    iotSimNumber: "",
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [instructionOpen, setInstructionOpen] = useState(false);
  const fileInputRef = useRef(null);
  const itemPreset = getImportPresetForItem(item);
  const expectedColumns = useMemo(() => {
    if (!item?.isSerialized) return ["name", "quantity", "unit", "minStock", "sku", "notes"];
    if (isGpsCategory(item?.category)) return ["name", "imei", "modelType", "sku", "notes"];
    if (isSimCategory(item?.category)) return ["name", "simNumber", "imsiNumber", "provider", "simType", "iotSimNumber", "sku", "notes"];
    return ["name", "serial", "sku", "notes"];
  }, [item]);

  const parse = (txt) =>
    String(txt || "")
      .split(/[\n, ]+/g)
      .map((x) => x.trim())
      .filter(Boolean);

  const readUploadRows = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
  };

  const validateRowNameForCurrentItem = (row, index) => {
    const incomingName = normalizeImportValue(row?.name);
    if (!incomingName) return null;
    if (incomingName.toLowerCase() !== normalizeImportValue(item?.name).toLowerCase()) {
      return `Row ${index + 2}: name must match selected item "${item?.name}"`;
    }
    return null;
  };

  const handleStockFileUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const rows = await readUploadRows(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("File me koi row nahi mili");
      }

      if (item?.isSerialized) {
        if (isGpsCategory(item?.category)) {
          const units = rows.map((row, index) => {
            const nameError = validateRowNameForCurrentItem(row, index);
            if (nameError) throw new Error(nameError);
            const imei = normalizeImportValue(row?.imei).replace(/\s+/g, "");
            const modelType = normalizeImportValue(row?.modelType);
            if (!/^\d{15}$/.test(imei)) throw new Error(`Row ${index + 2}: IMEI must be 15 digits`);
            if (!modelType) throw new Error(`Row ${index + 2}: modelType is required`);
            return { serial: imei, imei, modelType, notes: normalizeImportValue(row?.notes) };
          });
          await onSubmit({ units });
        } else if (isSimCategory(item?.category)) {
          const units = rows.map((row, index) => {
            const nameError = validateRowNameForCurrentItem(row, index);
            if (nameError) throw new Error(nameError);
            const simNumber = normalizeImportValue(row?.simNumber);
            const imsiNumber = normalizeImportValue(row?.imsiNumber);
            const provider = normalizeImportValue(row?.provider);
            const simType = normalizeImportValue(row?.simType);
            const iotSimNumber = normalizeImportValue(row?.iotSimNumber);
            if (!simNumber || !imsiNumber || !provider || !simType || !iotSimNumber) {
              throw new Error(`Row ${index + 2}: simNumber, imsiNumber, provider, simType, iotSimNumber required`);
            }
            return {
              serial: simNumber,
              simNumber,
              imsiNumber,
              provider,
              simType,
              iotSimNumber,
              notes: normalizeImportValue(row?.notes),
            };
          });
          await onSubmit({ units });
        } else {
          const units = rows.map((row, index) => {
            const nameError = validateRowNameForCurrentItem(row, index);
            if (nameError) throw new Error(nameError);
            const serial = normalizeImportValue(row?.serial);
            if (!serial) throw new Error(`Row ${index + 2}: serial is required`);
            return { serial, notes: normalizeImportValue(row?.notes) };
          });
          await onSubmit({ units });
        }
      } else {
        const totalQty = rows.reduce((sum, row, index) => {
          const nameError = validateRowNameForCurrentItem(row, index);
          if (nameError) throw new Error(nameError);
          const n = Math.max(0, Number(row?.quantity || 0));
          if (!Number.isFinite(n) || n <= 0) throw new Error(`Row ${index + 2}: quantity must be greater than 0`);
          return sum + n;
        }, 0);
        await onSubmit({ qty: totalQty });
      }
    } catch (error) {
      throw error;
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = () => {
    if (item?.isSerialized) {
      if (isGpsCategory(item?.category)) {
        if (!extra.imei.trim() || !extra.modelType.trim()) return;
        return onSubmit({
          units: [{
            serial: extra.imei,
            imei: extra.imei,
            modelType: extra.modelType,
          }],
        });
      }
      if (isSimCategory(item?.category)) {
        if (!extra.simNumber.trim() || !extra.imsiNumber.trim() || !extra.provider.trim() || !extra.simType.trim() || !extra.iotSimNumber.trim()) return;
        return onSubmit({
          units: [{
            serial: extra.simNumber,
            simNumber: extra.simNumber,
            imsiNumber: extra.imsiNumber,
            provider: extra.provider,
            simType: extra.simType,
            iotSimNumber: extra.iotSimNumber,
          }],
        });
      }
      const serials = parse(serialsText);
      if (!serials.length) return;
      onSubmit({ units: serials.map(s => ({ serial: s })) });
    } else {
      onSubmit({ qty: Math.max(1, Number(qty || 1)) });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Stock In: {item?.name || "-"}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Yahan ek-ek karke bhi add kar sakte ho, ya file upload se ek saath multiple stock add kar sakte ho.
        </Alert>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Stock upload instructions">
            <IconButton onClick={() => setInstructionOpen(true)}>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={async () => {
              try {
                await downloadInventoryTemplate(itemPreset);
              } catch (error) {
                window.alert(error?.response?.data?.msg || "Template download failed");
              }
            }}
            disabled={busy || uploadingFile}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || uploadingFile}
          >
            {uploadingFile ? "Uploading..." : "Upload Into This Stock"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={async (event) => {
              try {
                await handleStockFileUpload(event);
              } catch (error) {
                window.alert(error?.message || "Stock upload failed");
              }
            }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Expected columns: {expectedColumns.join(", ")}
        </Typography>
        <UploadInstructionsDialog
          open={instructionOpen}
          onClose={() => setInstructionOpen(false)}
          title={`Stock In Upload Instructions: ${item?.name || "Selected Item"}`}
          subtitle="Use this upload only for the currently opened inventory item. It adds stock inside the selected item."
          requiredColumns={expectedColumns}
          hinglish={[
            "Ye upload sirf abhi open item ke liye hai. Isse kisi doosre item me stock nahi jayega.",
            "Agar file me `name` column bhar rahe ho to usme selected item ka exact same name hona chahiye.",
            "Bulk item me quantity add hogi. Serialized item me new IMEI / SIM / serial add honge.",
            "File upload tab use karo jab ek hi item me bahut saari rows ek saath stock-in karni ho.",
            isGpsCategory(item?.category)
              ? "GPS ke liye har row me valid 15 digit IMEI aur `modelType` dena zaroori hai."
              : isSimCategory(item?.category)
                ? "SIM ke liye `simNumber`, `imsiNumber`, `provider`, `simType`, `iotSimNumber` sab required hain."
                : item?.isSerialized
                  ? "Serialized item me har row ka `serial` unique hona chahiye."
                  : "Bulk item me har row ka `quantity` 0 se bada hona chahiye.",
          ]}
          english={[
            "This upload is only for the currently opened item. It will not add stock to any other item.",
            "If you fill the `name` column, it must exactly match the selected item name.",
            "Bulk items will increase quantity. Serialized items will add new IMEI / SIM / serial units.",
            "Use this file upload when you want to stock-in many rows into the same item at once.",
            isGpsCategory(item?.category)
              ? "For GPS items, every row must contain a valid 15-digit IMEI and `modelType`."
              : isSimCategory(item?.category)
                ? "For SIM items, `simNumber`, `imsiNumber`, `provider`, `simType`, and `iotSimNumber` are all required."
                : item?.isSerialized
                  ? "For serialized items, each `serial` must be unique."
                  : "For bulk items, each `quantity` must be greater than zero.",
          ]}
        />
        {item?.isSerialized ? (
          isGpsCategory(item?.category) ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="IMEI Number *"
                value={extra.imei}
                onChange={(e) => setExtra((prev) => ({ ...prev, imei: e.target.value.replace(/\D/g, "").slice(0, 15) }))}
              />
              <TextField
                select
                fullWidth
                label="Model Type *"
                value={extra.modelType}
                onChange={(e) => setExtra((prev) => ({ ...prev, modelType: e.target.value }))}
                helperText="Uses the same model list available during device registration"
              >
                <MenuItem value="">Select model</MenuItem>
                {modelOptions.map((model) => (
                  <MenuItem key={model} value={model}>{model}</MenuItem>
                ))}
              </TextField>
            </Stack>
          ) : isSimCategory(item?.category) ? (
            <Stack spacing={2}>
              <TextField fullWidth label="SIM Number *" value={extra.simNumber} onChange={(e) => setExtra((prev) => ({ ...prev, simNumber: e.target.value }))} />
              <TextField fullWidth label="IMSI Number *" value={extra.imsiNumber} onChange={(e) => setExtra((prev) => ({ ...prev, imsiNumber: e.target.value }))} />
              <TextField fullWidth label="Provider *" value={extra.provider} onChange={(e) => setExtra((prev) => ({ ...prev, provider: e.target.value }))} />
              <TextField fullWidth label="SIM Type *" value={extra.simType} onChange={(e) => setExtra((prev) => ({ ...prev, simType: e.target.value }))} />
              <TextField fullWidth label="Actual IoT SIM Number *" value={extra.iotSimNumber} onChange={(e) => setExtra((prev) => ({ ...prev, iotSimNumber: e.target.value }))} />
            </Stack>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Serial/IMEI (paste multiple)"
              value={serialsText}
              onChange={(e) => setSerialsText(e.target.value)}
              placeholder={"Example:\n868000111222333\n868000111222334\nSN-12345"}
            />
          )
        ) : (
          <TextField fullWidth type="number" label="Qty" value={qty} onChange={(e) => setQty(e.target.value)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignTransferDialog({ open, mode, item, busy, onClose, onSubmit }) {
  const isTransfer = mode === "transfer";
  const isSerialized = !!item?.isSerialized;
  const suggestedOwnerType = isElectronicsCategory(item?.category) ? "vehicle" : "employee";

  const [serial, setSerial] = useState("");
  const [qty, setQty] = useState(1);
  const [ownerType, setOwnerType] = useState(suggestedOwnerType);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const unitStatus = isTransfer ? "assigned" : "in_stock";

  const [triggerSearch, { data, isFetching }] = useLazySearchOwnersQuery();
  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: item?._id, status: unitStatus },
    { skip: !open || !item?._id || !isSerialized }
  );
  const availableUnits = Array.isArray(unitsData?.units) ? unitsData.units : [];
  const bulkInStock = Math.max(0, Number(item?.quantityOnHand || 0));

  useEffect(() => {
    if (!open || ownerType === "warehouse") return;
    triggerSearch({ type: ownerType, q: "" });
  }, [open, ownerType, triggerSearch]);

  useEffect(() => {
    if (!open) return;
    setSerial("");
    setQty(1);
    setOwnerType(suggestedOwnerType);
    setSelectedOwner(null);
    triggerSearch({ type: suggestedOwnerType, q: "" });
  }, [open, suggestedOwnerType, triggerSearch]);

  const ownerTypeOptions = useMemo(() => {
    if (ownerType === "warehouse") return OWNER_TYPES;
    if (isElectronicsCategory(item?.category)) return OWNER_TYPES;
    return OWNER_TYPES.filter((option) => option.value !== "device");
  }, [item?.category, ownerType]);

  const selectedOwnerTypeMeta = useMemo(
    () => ownerTypeOptions.find((option) => option.value === ownerType) || OWNER_TYPES.find((option) => option.value === ownerType),
    [ownerType, ownerTypeOptions]
  );

  const owners = useMemo(() => {
    if (!data || data?.type !== ownerType) return [];
    return Array.isArray(data?.owners) ? data.owners : [];
  }, [data, ownerType]);

  const submitDisabled =
    busy ||
    (isSerialized ? !serial.trim() : Math.max(1, Number(qty || 1)) > bulkInStock) ||
    (ownerType !== "warehouse" && !selectedOwner?._id);

  const submit = () => {
    // ✅ validate
    if (isSerialized) {
      if (!serial.trim()) return;
    } else {
      const n = Math.max(1, Number(qty || 1));
      if (!n || n > bulkInStock) return;
    }

    // warehouse
    if (ownerType === "warehouse") {
      if (isTransfer) {
        return onSubmit(
          isSerialized
            ? { serial: serial.trim(), toOwnerType: "warehouse", toOwnerId: null, toOwnerLabel: "Warehouse" }
            : { qty: Math.max(1, Number(qty || 1)), toOwnerType: "warehouse", toOwnerId: null, toOwnerLabel: "Warehouse" }
        );
      }

      return onSubmit(
        isSerialized
          ? { ownerType: "warehouse", ownerId: null, ownerLabel: "Warehouse", serial: serial.trim() }
          : { ownerType: "warehouse", ownerId: null, ownerLabel: "Warehouse", qty: Math.max(1, Number(qty || 1)) }
      );
    }

    // vehicle (select from list or manual label)
    if (ownerType === "vehicle") {
      const label = selectedOwner?.meta?.vehicleNumber || selectedOwner?.label || "";
      if (!label) return;

      if (isTransfer) {
        return onSubmit(
          isSerialized
            ? { serial: serial.trim(), toOwnerType: "vehicle", toOwnerId: null, toOwnerLabel: label }
            : { qty: Math.max(1, Number(qty || 1)), toOwnerType: "vehicle", toOwnerId: null, toOwnerLabel: label }
        );
      }

      return onSubmit(
        isSerialized
          ? { ownerType: "vehicle", ownerId: null, ownerLabel: label, serial: serial.trim() }
          : { ownerType: "vehicle", ownerId: null, ownerLabel: label, qty: Math.max(1, Number(qty || 1)) }
      );
    }

    // other owners require selectedOwner
    if (!selectedOwner?._id) return;

    const label = selectedOwner.label;

    if (isTransfer) {
      return onSubmit(
        isSerialized
          ? { serial: serial.trim(), toOwnerType: ownerType, toOwnerId: selectedOwner._id, toOwnerLabel: label }
          : { qty: Math.max(1, Number(qty || 1)), toOwnerType: ownerType, toOwnerId: selectedOwner._id, toOwnerLabel: label }
      );
    }

    return onSubmit(
      isSerialized
        ? { ownerType, ownerId: selectedOwner._id, ownerLabel: label, serial: serial.trim() }
        : { ownerType, ownerId: selectedOwner._id, ownerLabel: label, qty: Math.max(1, Number(qty || 1)) }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{isTransfer ? "Transfer" : "Assign"}: {item?.name || "-"}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            {isTransfer
              ? "Step 1: assigned unit select karo. Step 2: jis owner ko dena hai wo dropdown se select karo."
              : "Step 1: available stock/unit select karo. Step 2: owner type aur owner dropdown se select karo."}
          </Alert>

          <Alert severity="info">
            Owner Type ka matlab:
            {" "}
            `Warehouse` = central stock,
            {" "}
            `Employee / Staff` = team member,
            {" "}
            `Customer / User` = end customer,
            {" "}
            `Vehicle` = vehicle installation.
            {isElectronicsCategory(item?.category) ? " Is category ke liye vehicle assignment recommended hai." : ""}
          </Alert>

          {isSerialized ? (
            <>
              <TextField
                select
                label={isTransfer ? "Assigned Serial/IMEI *" : "Available Serial/IMEI *"}
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                helperText={
                  unitsLoading
                    ? "Loading units..."
                    : availableUnits.length
                      ? `${availableUnits.length} unit(s) available`
                      : isTransfer
                        ? "No assigned unit available to transfer"
                        : "No in-stock unit available to assign"
                }
              >
                <MenuItem value="">-- Select --</MenuItem>
                {availableUnits.map((unit) => {
                  const value = unit?.imei || unit?.serial || unit?._id;
                  const label = unit?.imei
                    ? `${unit.imei}${unit?.serial && unit.serial !== unit.imei ? ` • ${unit.serial}` : ""}`
                    : unit?.serial || "Unknown unit";
                  return (
                    <MenuItem key={unit._id} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </>
          ) : (
            <TextField
              type="number"
              label="Qty *"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputProps={{ min: 1 }}
              helperText={`Available stock: ${bulkInStock}`}
            />
          )}

          <TextField
            select
            label={isTransfer ? "To Owner Type" : "Owner Type"}
            value={ownerType}
            onChange={(e) => {
              setOwnerType(e.target.value);
              setSelectedOwner(null);
            }}
            helperText={selectedOwnerTypeMeta?.description || ""}
          >
            {ownerTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>

          {ownerType !== "warehouse" && (
            <TextField
              select
              label={ownerType === "vehicle" ? "Select Vehicle *" : "Select Owner *"}
              value={selectedOwner?._id || ""}
              onChange={(e) => {
                const found = owners.find(x => String(x._id) === String(e.target.value));
                setSelectedOwner(found || null);
              }}
              helperText={
                isFetching
                  ? "Loading names..."
                  : owners.length
                    ? `${owners.length} option(s) available`
                    : "No option available"
              }
            >
              <MenuItem value="">-- Select --</MenuItem>
              {owners.map(o => (
                <MenuItem key={o._id} value={o._id}>{o.label}</MenuItem>
              ))}
            </TextField>
          )}

          {selectedOwner && (
            <TextField
              fullWidth
              label="Selected"
              value={selectedOwner.label}
              InputProps={{ readOnly: true }}
            />
          )}

          {!isSerialized && (
            <Alert severity={isTransfer ? "warning" : "info"}>
              {isTransfer
                ? "Bulk transfer direct supported nahi hai. Pehle return to warehouse karo, phir next owner ko assign karo."
                : "Bulk flow active. Qty-based assign use hoga."}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitDisabled}>
          {busy ? <CircularProgress size={18} /> : (isTransfer ? "Transfer" : "Assign")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function ReturnDialogWrapper({ open, item, busy, onClose, onSubmit }) {
  const [serial, setSerial] = useState("");
  const [qty, setQty] = useState(1);
  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: item?._id, status: "assigned" },
    { skip: !open || !item?._id || !item?.isSerialized }
  );
  const assignedUnits = Array.isArray(unitsData?.units) ? unitsData.units : [];

  useEffect(() => {
    if (!open) return;
    setSerial("");
    setQty(1);
  }, [open]);

  const submit = () => {
    if (item?.isSerialized) {
      if (!serial.trim()) return;
      onSubmit({ serial: serial.trim() });
    } else {
      onSubmit({ qty: Math.max(1, Number(qty || 1)) });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Return to Warehouse: {item?.name || "-"}</DialogTitle>
      <DialogContent dividers>
        {item?.isSerialized ? (
          <>
            <TextField
              fullWidth
              select
              label="Assigned Serial/IMEI"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              helperText={unitsLoading ? "Loading assigned units..." : `${assignedUnits.length} assigned unit(s) found`}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">-- Select --</MenuItem>
              {assignedUnits.map((unit) => {
                const value = unit?.imei || unit?.serial || unit?._id;
                const label = unit?.imei
                  ? `${unit.imei}${unit?.serial && unit.serial !== unit.imei ? ` • ${unit.serial}` : ""}`
                  : unit?.serial || "Unknown unit";
                return (
                  <MenuItem key={unit._id} value={value}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField fullWidth label="Or type Serial/IMEI manually" value={serial} onChange={(e) => setSerial(e.target.value)} />
          </>
        ) : (
          <TextField fullWidth type="number" label="Qty" value={qty} onChange={(e) => setQty(e.target.value)} helperText="Assigned bulk qty me se itna stock warehouse me return hoga" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Return"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ open, item, busy, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Delete Item?</DialogTitle>
      <DialogContent dividers>
        <Typography>Delete <b>{item?.name}</b>? (soft delete)</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
